/**
 * Change Provider Email Dialog
 *
 * Dialog for moderators to change the email a provider signs in with. This is
 * an identity change, not a profile edit: it rewrites the Auth0 login
 * credential as well as the user record.
 *
 * Two pieces of deliberate friction guard it:
 *   1. The address must be entered twice, so a typo can't silently lock a
 *      customer out of an address nobody can reach.
 *   2. The moderator must attest they confirmed the change with the customer
 *      over text message. The server rejects the request without it, so this
 *      checkbox is a real gate rather than UI decoration.
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  TextField,
  Typography,
} from '@mui/material';
import { useSnackbar } from 'notistack';

import { useChangeProviderEmailMutation } from '../store/hooks';

interface ChangeProviderEmailDialogProps {
  open: boolean;
  onClose: () => void;
  providerId: string;
  providerName: string;
  currentEmail: string | null | undefined;
}

// Intentionally loose — matches the backend's permissive check. Auth0 is the
// real authority on deliverability; this only catches obvious mistakes.
const EMAIL_PATTERN = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;

const normalize = (value: string) => value.trim().toLowerCase();

/**
 * Friendly names for the identity providers we actually see. The provider id
 * is the Auth0 sub, whose prefix is the connection strategy — `auth0|` means a
 * database (email/password) identity, anything else is external.
 */
const IDENTITY_PROVIDER_LABELS: Record<string, string> = {
  'google-oauth2': 'Google',
  windowslive: 'Microsoft',
  waad: 'Microsoft',
  samlp: 'single sign-on',
  adfs: 'single sign-on',
  oidc: 'single sign-on',
};

/**
 * Returns the sign-in provider's name when the account is NOT a
 * password-based one, or null when it is.
 *
 * We check client-side purely to fail fast: it costs no extra request, and it
 * means a moderator never gets as far as texting a customer about a change
 * that cannot happen. The server performs the same check against Auth0's
 * actual identity record and remains the authority.
 */
function externalIdentityProvider(providerId: string): string | null {
  const strategy = providerId.split('|')[0];
  if (!strategy || strategy === 'auth0') return null;
  return IDENTITY_PROVIDER_LABELS[strategy] ?? 'an external identity provider';
}

export function ChangeProviderEmailDialog({
  open,
  onClose,
  providerId,
  providerName,
  currentEmail,
}: ChangeProviderEmailDialogProps) {
  const { enqueueSnackbar } = useSnackbar();
  const [newEmail, setNewEmail] = useState('');
  const [confirmEmail, setConfirmEmail] = useState('');
  const [smsConfirmed, setSmsConfirmed] = useState(false);
  const [changeEmail, { isLoading }] = useChangeProviderEmailMutation();

  // The provider id is the Auth0 sub, so this needs no extra request.
  const identityProvider = externalIdentityProvider(providerId);

  // Clear every field — including the attestation — each time the dialog
  // opens, so a previous confirmation can never carry over to a new change.
  useEffect(() => {
    if (open) {
      setNewEmail('');
      setConfirmEmail('');
      setSmsConfirmed(false);
    }
  }, [open]);

  const trimmedNew = normalize(newEmail);
  const trimmedConfirm = normalize(confirmEmail);

  const isFormatValid = EMAIL_PATTERN.test(trimmedNew);
  const isUnchanged = !!currentEmail && trimmedNew === normalize(currentEmail);
  const emailsMatch = trimmedNew === trimmedConfirm;

  const newEmailError = useMemo(() => {
    if (!newEmail) return null;
    if (!isFormatValid) return 'Enter a valid email address';
    if (isUnchanged) return 'This is already the provider’s email address';
    return null;
  }, [newEmail, isFormatValid, isUnchanged]);

  const confirmEmailError = useMemo(() => {
    if (!confirmEmail) return null;
    if (!emailsMatch) return 'Email addresses do not match';
    return null;
  }, [confirmEmail, emailsMatch]);

  const canSubmit =
    isFormatValid && !isUnchanged && emailsMatch && !!trimmedConfirm && smsConfirmed && !isLoading;

  const handleClose = useCallback(() => {
    if (isLoading) return;
    onClose();
  }, [isLoading, onClose]);

  const handleSubmit = useCallback(async () => {
    if (!canSubmit) return;

    try {
      await changeEmail({
        providerId,
        changeProviderEmailRequest: {
          new_email: trimmedNew,
          sms_confirmed: smsConfirmed,
        },
      }).unwrap();

      enqueueSnackbar(`Login email changed to ${trimmedNew}`, { variant: 'success' });
      onClose();
    } catch (error: any) {
      enqueueSnackbar(
        error?.data?.message || error?.data?.detail || 'Failed to change email address',
        { variant: 'error' }
      );
    }
  }, [
    canSubmit,
    changeEmail,
    providerId,
    trimmedNew,
    smsConfirmed,
    enqueueSnackbar,
    onClose,
  ]);

  // Dead stop for externally-managed identities. Rendered instead of the form
  // — not merely disabling it — so there is nothing to fill in and, crucially,
  // no attestation to tick. The moderator learns this before contacting the
  // customer rather than after.
  if (identityProvider) {
    return (
      <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
        <DialogTitle>Change Login Email</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, pt: 2 }}>
            <Box>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Provider
              </Typography>
              <Typography variant="body1" sx={{ fontWeight: 600 }}>
                {providerName}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                Current email: {currentEmail || <em>None on file</em>}
              </Typography>
            </Box>

            <Alert severity="info">
              <AlertTitle sx={{ fontWeight: 600 }}>
                This provider signs in with {identityProvider}
              </AlertTitle>
              <Typography variant="body2">
                Their email address can’t be changed from JotPsych.
              </Typography>
            </Alert>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} variant="contained">
            Close
          </Button>
        </DialogActions>
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
      <DialogTitle>Change Login Email</DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, pt: 2 }}>
          <Box>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Provider
            </Typography>
            <Typography variant="body1" sx={{ fontWeight: 600 }}>
              {providerName}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              Current email: {currentEmail || <em>None on file</em>}
            </Typography>
          </Box>

          <Alert severity="warning">
            <AlertTitle sx={{ fontWeight: 600 }}>This changes how they sign in</AlertTitle>
            <Typography variant="body2">
              The provider will no longer be able to log in with their old address. Their password
              is unchanged, and no verification email is sent — your text-message confirmation below
              is what stands in for it.
            </Typography>
          </Alert>

          <TextField
            label="New email address"
            type="email"
            value={newEmail}
            onChange={e => setNewEmail(e.target.value)}
            disabled={isLoading}
            fullWidth
            autoComplete="off"
            error={!!newEmailError}
            helperText={newEmailError ?? ' '}
          />

          <TextField
            label="Confirm new email address"
            type="email"
            value={confirmEmail}
            onChange={e => setConfirmEmail(e.target.value)}
            disabled={isLoading}
            fullWidth
            autoComplete="off"
            onPaste={e => e.preventDefault()}
            error={!!confirmEmailError}
            helperText={confirmEmailError ?? 'Re-type the address — pasting is disabled here'}
          />

          <Box>
            <FormControlLabel
              control={
                <Checkbox
                  checked={smsConfirmed}
                  onChange={e => setSmsConfirmed(e.target.checked)}
                  disabled={isLoading}
                />
              }
              label="I confirmed this change with the customer via text message"
            />
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ display: 'block', ml: 4, mt: -0.5 }}
            >
              Required. This attestation is recorded against your admin account.
            </Typography>
          </Box>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={isLoading}>
          Cancel
        </Button>
        <Button onClick={handleSubmit} variant="contained" color="warning" disabled={!canSubmit}>
          {isLoading ? <CircularProgress size={20} /> : 'Change Email'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
